import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import { IDeleteApiResponse, IError, apiLimit } from "../../utils/api.types";
import { VocuherCategory } from "../../utils/common-utils";
import {InvoiceElementsInterface, AInvoiceUpdatePayloadNew } from '../../components/Invoice/InvoiceElementInterface';
import {
  InvoiceSeqNumber,
  EstimateSeqNumber,
  InvoiceFormInputs,
  InvoiceResponse,
  AInvoices,
  AInvoiceResponse,
  AInvoiceUpdatePayload,
  InvoiceFetch,
  InvoicePreviewResponse,
  InvoicePreviewPayload,
  ITemplateDefaults,
  ICustomTemplatePayload,
  PdfGenerationError,
  InvoiceDispatch,
  IDispatchApiResponse,
  ITemplate,
  ShareInvoiceForm,
  IShareInvoiceApiResponse,
  InvoiceLineItemResponse,
} from "../../utils/invoice.types";

import {
    EstimateFormInputs,
    EstimateResponse
  } from "../../utils/estimate.types";

// Fetch InvoiceSeqNumber async thunk.
export const fetchInvoiceSeqNumber = createAsyncThunk(
  'invoice/fetchInvoiceSeqNumber',
  async (voucherType: string): Promise<InvoiceSeqNumber> => {
      const response = await axios.get(`/api/v1/invoice/seq-number?voucherType=${voucherType}`);
      return response.data
  }
);

// Fetch InvoiceSeqNumber By Voucher Type async thunk.
export const fetchInvoiceSeqNumberByVoucherType = createAsyncThunk(
    'invoice/fetchInvoiceSeqNumberByVoucherType',
    async (voucherType: string): Promise<InvoiceSeqNumber> => {
        const response = await axios.get(`/api/v1/sales/credit-note/seq-number?saleType=${voucherType}`);
        return response.data
    }
);

//Add Invoice async Thunk.
 export const addInvoice = createAsyncThunk(
  'invoice/addInvoice',
  async (invoicePayload: InvoiceElementsInterface): Promise<AInvoiceResponse> => {
      const response = await axios.post(`/api/v1/invoice`, invoicePayload);

      return response.data
  }
);

/**
 * Update EstimateOrDelivery async Thunk.
 */
 export const updateEstimate = createAsyncThunk<
 AInvoiceResponse,
 AInvoiceUpdatePayload,
 {
     rejectValue: IError,
 }
>(
 'invoice/updateEstimate',
 async ({ id, invoicePayload }: AInvoiceUpdatePayload, thunkApi) => {
     try {
         const response = await axios.put(`/api/v1/invoice/${id}`, invoicePayload);

         const { data } = response;

         if (response.status === 200) {
             return data;
         }

         return thunkApi.rejectWithValue(data.error);
     } catch (error: any) {
         return thunkApi.rejectWithValue(error.response.data);
     }
 }
);

//Dispatch Invoice async Thunk.
export const dispatchInvoice = createAsyncThunk(
    'invoice/dispatchInvoice',
    async ({invoiceId, params, dispatchPayload}: InvoiceDispatch): Promise<IDispatchApiResponse> => {
        let queryParam = (params)? `?${params}`: ''
        const response = await axios.post(`/api/v1/invoice/${invoiceId}/dispatch${queryParam}`, dispatchPayload);
  
        return response.data
    }
  );


/**
 * Fetch Invoices async thunk.
*/
export const fetchInvoices = createAsyncThunk(
    'invoice/fetchInvoices',
    async ({accountId, type, params} : InvoiceFetch): Promise<AInvoices> => {
        let path = (type===VocuherCategory.SALE)? `${accountId}/sales` : `${accountId}/purchases`
        let queryParam = (params)? `${params}`: ''
        const response = await axios.get(`/api/v1/invoice/account/${path}?${queryParam}`);
        return response.data;
    }
);

export const fetchInvoicesWithoutState = createAsyncThunk(
    'invoice/fetchInvoicesWithoutState',
    async ({accountId, type, params} : InvoiceFetch): Promise<AInvoices> => {
        let path = (type===VocuherCategory.SALE)? `${accountId}/account-receivables` : `${accountId}/account-payables`
        let queryParam = (params)? `&${params}`: ''
        const response = await axios.get(`/api/v1/invoice/account/${path}?limit=${apiLimit.limit}${queryParam}`);
        return response.data;
    }
);

export const selectedInvoiceID = createAction("invoice/selectedInvoiceID", (invoiceId) => {
    return {
        type: 'invoice/selectedInvoiceID',
        payload: invoiceId
    }
});


/**
 * Update Invoices async Thunk.
 */
export const updateInvoice = createAsyncThunk<
    AInvoiceResponse,
    AInvoiceUpdatePayloadNew,
    {
        rejectValue: IError,
    }
>(
    'invoice/updateInvoice',
    async ({ id, invoicePayload }: AInvoiceUpdatePayloadNew, thunkApi) => {
        try {
            const response = await axios.put(`/api/v1/invoice/${id}`, invoicePayload);

            const { data } = response;

            if (response.status === 200) {
                return data;
            }

            return thunkApi.rejectWithValue(data.error);
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response.data);
        }
    }
);

export const updateInvoiceStatus = createAsyncThunk(
    'invoice/updateInvoiceStatus',
    async ({ id, invoiceStatus }: any, thunkApi) => {
        try {
            let payload = {status: invoiceStatus};
            console.log(payload)
            const response = await axios.put(`/api/v1/invoice/${id}/updateStatus`, payload);
            const { data } = response;
            if (response.status === 200) {
                return data;
            }
            return thunkApi.rejectWithValue(data.error);
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response.data);
        }
    }
);


/**
 * Fetch Invoices async thunk.
*/
export const fetchInvoiceById = createAsyncThunk(
    'invoice/fetchInvoiceById',
    async (id: string): Promise<AInvoices> => {
        const response = await axios.get(`/api/v1/invoice/${id}`);
        return response.data;
    }
);

export const fetchSelectedInvoicesLineItem = createAsyncThunk(
    'invoice/fetchSelectedInvoicesLineItem',
    async (invoiceIds: any): Promise<InvoiceLineItemResponse> => {
        const response = await axios.post(`/api/v1/invoice/line-items/`, {invoiceIds: invoiceIds});
        return response.data;
    }
);

/**
 * Delete Invoices async Thunk.
 */
export const deleteInvoice = createAsyncThunk(
    'invoice/deleteInvoice',
    async (id: string): Promise<IDeleteApiResponse> => {
        const response = await axios.delete(`/api/v1/invoice/${id}`);

        response.data['id'] = id;

        return response.data
    }
);

/**
 * Delete Invoices async Thunk.
 */
 export const manualPayment = createAsyncThunk(
    'invoice/manualPayment',
    async (payload: any): Promise<IDeleteApiResponse> => {
        const response = await axios.post('/api/v1/invoice/vendor-payment-application', payload);
        return response.data
    }
);

export const fetchInvoicePreview = createAsyncThunk<InvoicePreviewResponse,
InvoicePreviewPayload,
{
    rejectValue: IError,
}
 >(
    'invoice/preview',
    async ({invoiceId, templateId}: InvoicePreviewPayload, thunkApi) => {

        try {
            const response = await axios.get(`/api/v1/invoice/${invoiceId}/templates/${templateId}`);
            const { data } = response;
            if (response.status === 200) {
                return data;
            }
            return thunkApi.rejectWithValue(data.error);
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response.data);
        }
       
    }
);

/**
 * Force re-generate the invoice PDF for a template. Rejects with a PdfGenerationError
 * (missingFields / errorDetail) when the backend cannot build the PDF.
 */
export const regenerateInvoicePdf = createAsyncThunk<InvoicePreviewResponse,
InvoicePreviewPayload,
{
    rejectValue: PdfGenerationError,
}
 >(
    'invoice/regeneratePdf',
    async ({invoiceId, templateId}: InvoicePreviewPayload, thunkApi) => {
        try {
            const response = await axios.get(`/api/v1/invoice/${invoiceId}/templates/${templateId}?regenerate=true`);
            return response.data;
        } catch (error: any) {
            const data = error?.response?.data;
            return thunkApi.rejectWithValue(
                data && typeof data === 'object'
                    ? data
                    : { message: error?.message || 'PDF generation failed.' }
            );
        }
    }
);

/**
 * Fetch Invoice Payment Receipts PDF async Thunk.
 */

export const fetchInvoicePaymentReceiptPreview = createAsyncThunk<InvoicePreviewResponse,
InvoicePreviewPayload,
{
    rejectValue: IError,
}
 >(
    'invoice-payment-receipt/preview',
    async ({invoiceId, templateId}: InvoicePreviewPayload, thunkApi) => {

        try {
            const response = await axios.get(`/api/v1/invoice/${invoiceId}/payment-receipt-templates/${templateId}`);
            const { data } = response;
            if (response.status === 200) {
                return data;
            }
            return thunkApi.rejectWithValue(data.error);
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response.data);
        }
       
    }
);


/**
 * Send Invoice Payment Receipts async Thunk.
 */

export const shareInvoicePaymentReceipt = createAsyncThunk(
    'invoice/sendInvoceReceipt',
    async (data: ShareInvoiceForm): Promise<IShareInvoiceApiResponse> => {
        const response = await axios.post('/api/v1/invoice/share', data);
        return response.data;
    }
);


/**
 * Fetch invoice templates async thunk.
*/
export const fetchTemplates = createAsyncThunk(
    'invoice/fetchTemplates',
    async (voucherType: string | undefined = undefined): Promise<ITemplate[]> => {
        const response = await axios.get(`/api/v1/invoice/templates`, { params: voucherType ? { voucherType } : undefined });
        return response.data;
    }
);

// User-built voucher templates (Settings > Templates).
export const fetchCustomTemplates = createAsyncThunk(
    'invoice/fetchCustomTemplates',
    async (): Promise<ITemplate[]> => {
        const response = await axios.get(`/api/v1/invoice/custom-templates`);
        return response.data;
    }
);

export const fetchCustomTemplateDefaults = createAsyncThunk(
    'invoice/fetchCustomTemplateDefaults',
    async (voucherType: string | undefined = undefined): Promise<ITemplateDefaults> => {
        const response = await axios.get(`/api/v1/invoice/custom-templates/defaults`, { params: voucherType ? { voucherType } : undefined });
        return response.data;
    }
);

export const saveCustomTemplate = createAsyncThunk<ITemplate, ICustomTemplatePayload, { rejectValue: IError }>(
    'invoice/saveCustomTemplate',
    async ({ id, ...payload }, thunkApi) => {
        try {
            const response = id
                ? await axios.put(`/api/v1/invoice/custom-templates/${id}`, payload)
                : await axios.post(`/api/v1/invoice/custom-templates`, payload);
            return response.data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response?.data);
        }
    }
);

export const deleteCustomTemplate = createAsyncThunk<{ message: string }, string, { rejectValue: IError }>(
    'invoice/deleteCustomTemplate',
    async (id, thunkApi) => {
        try {
            const response = await axios.delete(`/api/v1/invoice/custom-templates/${id}`);
            return response.data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response?.data);
        }
    }
);


// Fetch Invoices by Invoice Number async Thunk.
export const fetchInvoicesByInvNumber  = createAsyncThunk(
    'invoices/search',
    async (data: any): Promise<AInvoices> => {
        const {name, voucherType} = data;
        const response = await axios.get(`/api/v1/invoice/fetch-invoices-by-query?name=${name}&&voucherType=${voucherType}`);
        return response.data;
    }
);

export const deletePaymentReceipt = createAsyncThunk(
    'invoice/deletePaymentReceipt',
    async ({ id, deletePayment = false }: { id: string; deletePayment?: boolean }): Promise<IDeleteApiResponse> => {
        const response = await axios.delete(`/api/v1/invoice/payment-receipt?invoiceId=${id}&deletePayment=${deletePayment}`);
        response.data['id'] = id;
        return response.data
    }
);